import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  StopOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import {
  centsToEuro,
  decimalInputProps,
  euroToCents,
  formatDay,
  formatEuro
} from "../workReports/format";
import {
  activateContract,
  contractPdfUrl,
  createContract,
  deleteContract,
  getContract,
  listInstallations,
  terminateContract,
  updateContract
} from "./api";
import {
  CONTRACT_STATUS_COLORS,
  contractStatusLabels,
  intervalLabels,
  visitStatusLabels
} from "./labels";

const toDay = value => (value ? dayjs(value) : null);
const fromDay = value => (value ? value.format("YYYY-MM-DD") : "");
// Fields the server only accepts while the contract is a draft
const DRAFT_ONLY = [
  "client",
  "clientBranch",
  "billingMode",
  "flatInterval",
  "startDate",
  "termMonths",
  "autoRenew",
  "renewalMonths",
  "endDate",
  "firstDue",
  "dueMode"
];

const ContractDrawer = ({ open, id, meta, onClose, onSaved }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [record, setRecord] = useState(null);
  const [installations, setInstallations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [termination, setTermination] = useState({
    receivedOn: dayjs(),
    effective: null,
    reason: ""
  });
  const client = Form.useWatch("client", form);
  const billingMode = Form.useWatch("billingMode", form);
  const autoRenew = Form.useWatch("autoRenew", form);
  const statuses = contractStatusLabels(t);
  const visitStatuses = visitStatusLabels(t);
  const intervals = intervalLabels(t);
  const status = record?.status || "draft";
  const canEdit = (id ? meta?.can.update : meta?.can.create) && status !== "ended";
  const locked = name => status !== "draft" && DRAFT_ONLY.includes(name);

  const fill = data =>
    form.setFieldsValue({
      ...data,
      clientBranch: data.clientBranch || null,
      startDate: toDay(data.startDate),
      firstDue: toDay(data.firstDue),
      endDate: toDay(data.endDate),
      pricePerVisit: centsToEuro(data.pricePerVisit),
      annualFee: centsToEuro(data.annualFee),
      installations: (data.installations || []).map(String),
      employees: (data.employees || []).map(String),
      notifyCustomer: data.notifyCustomer ?? meta?.settings.notifyCustomer
    });

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setRecord(null);
    if (id) {
      getContract(id)
        .then(res => {
          setRecord(res.data.data);
          fill(res.data.data);
        })
        .catch(generalErrorAlert);
    } else {
      form.setFieldsValue({
        intervalMonths: 12,
        dueMode: "schedule",
        billingMode: "per_visit",
        flatInterval: "yearly",
        taxRate: 19,
        startDate: dayjs(),
        termMonths: 12,
        autoRenew: true,
        renewalMonths: 12,
        noticeMonths: 3,
        estimatedMinutes: 60,
        scope: meta?.settings.contractScope || "",
        notifyCustomer: meta?.settings.notifyCustomer
      });
    }
  }, [open, id]);

  useEffect(() => {
    if (!open || !client) return setInstallations([]);
    listInstallations({ client, active: "all" })
      .then(res => setInstallations(res.data.data))
      .catch(generalErrorAlert);
  }, [open, client]);

  const clientInfo = meta?.clients.find(c => c._id === client);

  const bodyOf = values => {
    const body = {
      ...values,
      startDate: fromDay(values.startDate),
      firstDue: fromDay(values.firstDue),
      endDate: fromDay(values.endDate),
      pricePerVisit: euroToCents(values.pricePerVisit),
      annualFee: euroToCents(values.annualFee),
      clientBranch: values.clientBranch || null,
      leadDays: values.leadDays ?? null
    };
    if (status !== "draft") DRAFT_ONLY.forEach(key => delete body[key]);
    return body;
  };

  const save = async values => {
    setBusy(true);
    try {
      const body = bodyOf(values);
      const res = id ? await updateContract(id, body) : await createContract(body);
      Success(t("Saved"));
      setRecord(res.data.data);
      fill(res.data.data);
      onSaved(res.data.data, !id);
      return res.data.data;
    } catch (error) {
      generalErrorAlert(error);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    try {
      const values = await form.validateFields();
      await updateContract(id, bodyOf(values));
      const res = await activateContract(id);
      Success(t("Contract activated"));
      setRecord(res.data.data);
      fill(res.data.data);
      onSaved(res.data.data);
    } catch (error) {
      if (!error?.errorFields) generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const terminate = async () => {
    setBusy(true);
    try {
      const res = await terminateContract(id, {
        receivedOn: fromDay(termination.receivedOn),
        effective: fromDay(termination.effective),
        reason: termination.reason
      });
      setTerminateOpen(false);
      setRecord(res.data.data);
      fill(res.data.data);
      onSaved(res.data.data);
      Success(
        t("Terminated effective {{date}}", { date: formatDay(res.data.data.termination.effective) })
      );
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await deleteContract(id);
      Success(t("Deleted"));
      onSaved(null);
      onClose();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const money = name => (
    <InputNumber
      className="mnt-full"
      min={0}
      precision={2}
      addonAfter="€"
      {...decimalInputProps(lng)}
      disabled={locked(name) || !canEdit}
    />
  );

  return (
    <Drawer
      rootClassName="maintenance-page"
      open={open}
      width={isMobile ? "100%" : 680}
      title={
        id ? (
          <Space wrap>
            <span>{record?.number}</span>
            <Tag color={CONTRACT_STATUS_COLORS[status]}>{statuses[status]}</Tag>
          </Space>
        ) : (
          t("New maintenance contract")
        )
      }
      onClose={onClose}
      destroyOnClose
      footer={
        <div className="mnt-drawer-footer">
          {id && (
            <Button icon={<FilePdfOutlined />} href={contractPdfUrl(id)} target="_blank">
              {t("PDF")}
            </Button>
          )}
          {id && status === "draft" && meta?.can.delete && (
            <Popconfirm title={t("Delete this draft?")} onConfirm={remove}>
              <Button danger icon={<DeleteOutlined />}>
                {t("Delete")}
              </Button>
            </Popconfirm>
          )}
          {id && status === "active" && meta?.can.update && (
            <Button icon={<StopOutlined />} onClick={() => setTerminateOpen(true)}>
              {t("Terminate")}
            </Button>
          )}
          {canEdit && (
            <Button loading={busy} onClick={() => form.submit()}>
              {t("Save")}
            </Button>
          )}
          {id && status === "draft" && meta?.can.update && (
            <Button type="primary" icon={<CheckCircleOutlined />} loading={busy} onClick={activate}>
              {t("Activate")}
            </Button>
          )}
        </div>
      }
    >
      {record && status !== "draft" && (
        <Descriptions size="small" column={1} bordered className="mnt-section-bottom">
          <Descriptions.Item label={t("Next visit due")}>
            {formatDay(record.nextDue) || "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("Current term ends")}>
            {formatDay(record.term?.termEnd) || "—"}
          </Descriptions.Item>
          {record.term?.noticeDeadline && status === "active" && (
            <Descriptions.Item label={t("Notice must arrive by")}>
              {formatDay(record.term.noticeDeadline)}
            </Descriptions.Item>
          )}
          {record.termination?.effective && (
            <Descriptions.Item label={t("Terminated")}>
              {t("Notice of {{received}}, ends {{effective}}", {
                received: formatDay(record.termination.receivedOn),
                effective: formatDay(record.termination.effective)
              })}
            </Descriptions.Item>
          )}
          {record.recurringInfo && (
            <Descriptions.Item label={t("Flat fee billing")}>
              {t("{{amount}} {{interval}}, next invoice {{date}}", {
                amount: formatEuro(record.feePerPeriod, lng),
                interval: intervals[record.recurringInfo.interval] || "",
                date: record.recurringInfo.active ? formatDay(record.recurringInfo.nextDate) : "—"
              })}{" "}
              <Link to="/user/invoices?view=recurring">{t("Recurring invoices")}</Link>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}
      {status === "draft" && id && (
        <Alert
          className="mnt-section-bottom"
          type="info"
          showIcon
          message={t(
            "A draft plans nothing. Activate the contract to schedule visits and, for a flat fee, start the recurring invoice."
          )}
        />
      )}

      <Form form={form} layout="vertical" onFinish={save} disabled={!canEdit}>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="client" label={t("Customer")} rules={[{ required: true }]}>
              <Select
                showSearch
                disabled={locked("client")}
                optionFilterProp="label"
                options={(meta?.clients || []).map(c => ({ value: c._id, label: c.name }))}
                onChange={() => form.setFieldsValue({ clientBranch: null, installations: [] })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="clientBranch" label={t("Branch")}>
              <Select
                allowClear
                disabled={locked("clientBranch") || !clientInfo?.branches.length}
                placeholder={t("Main address")}
                options={(clientInfo?.branches || [])
                  .filter(b => b.active || b._id === record?.clientBranch)
                  .map(b => ({ value: b._id, label: b.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="title" label={t("Title")}>
              <Input maxLength={200} placeholder={t("e.g. Annual boiler service")} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="installations" label={t("Installations covered")}>
              <Select
                mode="multiple"
                optionFilterProp="label"
                placeholder={client ? t("Choose installations") : t("Choose the customer first")}
                options={installations.map(i => ({
                  value: i._id,
                  label: [i.name, i.location, i.branch?.name].filter(Boolean).join(" · "),
                  disabled: Boolean(
                    i.contract && i.contract._id !== id && i.contract.status !== "draft"
                  )
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="intervalMonths" label={t("Interval (months)")}>
              <InputNumber className="mnt-full" min={1} max={120} precision={0} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="firstDue" label={t("First visit due")} rules={[{ required: true }]}>
              <DatePicker
                className="mnt-full"
                format="DD.MM.YYYY"
                disabled={locked("firstDue") || !canEdit}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="dueMode" label={t("Next due counts from")}>
              <Segmented
                block
                disabled={locked("dueMode") || !canEdit}
                options={[
                  { value: "schedule", label: t("Fixed rhythm") },
                  { value: "completion", label: t("Inspection") }
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="scope" label={t("Scope of services")}>
              <Input.TextArea autoSize={{ minRows: 3, maxRows: 10 }} maxLength={10000} />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Typography.Title level={5}>{t("Price")}</Typography.Title>
          </Col>
          <Col xs={24}>
            <Form.Item name="billingMode">
              <Segmented
                block
                disabled={locked("billingMode") || !canEdit}
                options={[
                  { value: "per_visit", label: t("Per visit") },
                  { value: "annual_flat", label: t("Annual flat fee") }
                ]}
              />
            </Form.Item>
          </Col>
          {billingMode === "annual_flat" ? (
            <>
              <Col xs={12}>
                <Form.Item name="annualFee" label={t("Flat fee per year (net)")}>
                  {money("annualFee")}
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item name="flatInterval" label={t("Billed in advance")}>
                  <Select
                    disabled={locked("flatInterval") || !canEdit}
                    options={Object.entries(intervals).map(([value, label]) => ({ value, label }))}
                  />
                </Form.Item>
              </Col>
            </>
          ) : (
            <Col xs={12}>
              <Form.Item name="pricePerVisit" label={t("Price per visit (net)")}>
                {money("pricePerVisit")}
              </Form.Item>
            </Col>
          )}
          <Col xs={12}>
            <Form.Item name="taxRate" label={t("VAT rate")}>
              <Select options={[19, 7, 0].map(rate => ({ value: rate, label: `${rate} %` }))} />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Typography.Title level={5}>{t("Term")}</Typography.Title>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="startDate" label={t("Start")} rules={[{ required: true }]}>
              <DatePicker
                className="mnt-full"
                format="DD.MM.YYYY"
                allowClear={false}
                disabled={locked("startDate") || !canEdit}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="termMonths" label={t("First term (months)")}>
              <InputNumber
                className="mnt-full"
                min={1}
                max={120}
                precision={0}
                disabled={locked("termMonths") || !canEdit}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="autoRenew" label={t("Renews automatically")} valuePropName="checked">
              <Switch disabled={locked("autoRenew") || !canEdit} />
            </Form.Item>
          </Col>
          {autoRenew ? (
            <>
              <Col xs={12}>
                <Form.Item
                  name="renewalMonths"
                  label={t("Renewal (months)")}
                  extra={t("0 = runs on indefinitely, as required for consumers")}
                >
                  <InputNumber
                    className="mnt-full"
                    min={0}
                    max={60}
                    precision={0}
                    disabled={locked("renewalMonths") || !canEdit}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item name="noticeMonths" label={t("Notice period (months)")}>
                  <InputNumber className="mnt-full" min={0} max={24} precision={0} />
                </Form.Item>
              </Col>
            </>
          ) : (
            <Col xs={12}>
              <Form.Item name="endDate" label={t("Ends on")}>
                <DatePicker
                  className="mnt-full"
                  format="DD.MM.YYYY"
                  disabled={locked("endDate") || !canEdit}
                />
              </Form.Item>
            </Col>
          )}

          <Col xs={24}>
            <Typography.Title level={5}>{t("Planning")}</Typography.Title>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item
              name="leadDays"
              label={t("Plan days ahead")}
              extra={t("Empty = company setting")}
            >
              <InputNumber className="mnt-full" min={0} max={180} precision={0} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="estimatedMinutes" label={t("Duration (minutes)")}>
              <InputNumber className="mnt-full" min={15} max={1440} step={15} precision={0} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="notifyCustomer"
              label={t("Email date proposal")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="employees" label={t("Technicians")}>
              <Select
                mode="multiple"
                optionFilterProp="label"
                options={(meta?.employees || []).map(e => ({ value: e._id, label: e.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="notes" label={t("Internal notes")}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={5000} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {record?.visits?.length > 0 && (
        <>
          <Typography.Title level={5}>{t("Visits")}</Typography.Title>
          <List
            size="small"
            dataSource={record.visits}
            renderItem={visit => (
              <List.Item>
                <Space wrap>
                  <Link to={`/user/maintenance/visits/${visit._id}`}>{visit.number}</Link>
                  <span>{formatDay(visit.date || visit.plannedDate)}</span>
                  <Tag>{visitStatuses[visit.status]}</Tag>
                </Space>
              </List.Item>
            )}
          />
        </>
      )}

      <Modal
        rootClassName="maintenance-page"
        open={terminateOpen}
        title={t("Terminate contract")}
        okText={t("Terminate")}
        cancelText={t("Cancel")}
        confirmLoading={busy}
        onOk={terminate}
        onCancel={() => setTerminateOpen(false)}
      >
        <Form layout="vertical">
          <Form.Item label={t("Notice received on")} required>
            <DatePicker
              className="mnt-full"
              format="DD.MM.YYYY"
              allowClear={false}
              value={termination.receivedOn}
              onChange={value => setTermination(prev => ({ ...prev, receivedOn: value }))}
            />
          </Form.Item>
          <Form.Item
            label={t("Ends on")}
            extra={t("Empty = end of the term the notice period allows")}
          >
            <DatePicker
              className="mnt-full"
              format="DD.MM.YYYY"
              value={termination.effective}
              onChange={value => setTermination(prev => ({ ...prev, effective: value }))}
            />
          </Form.Item>
          <Form.Item label={t("Reason")}>
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 5 }}
              maxLength={1000}
              value={termination.reason}
              onChange={event => setTermination(prev => ({ ...prev, reason: event.target.value }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
};

export default ContractDrawer;
